import React, { useState } from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { Form, InputField } from "@mimber/forms"
import { Button, Typography, message } from "@mimber/ui-web"
import {
  newPasswordRequiredSchema,
  confirmSignIn,
  mapCognitoError,
} from "@mimber/auth"
import { fetchAuthSession } from "aws-amplify/auth"
import type {
  NewPasswordRequiredFormProps,
  NewPasswordRequiredFormData,
} from "./NewPasswordRequiredForm.types"

const { Paragraph, Title } = Typography

export const NewPasswordRequiredForm: React.FunctionComponent<
  NewPasswordRequiredFormProps
> = ({ onSuccess, onCancel }) => {
  const [isLoading, setIsLoading] = useState(false)

  const form = useForm<NewPasswordRequiredFormData>({
    resolver: zodResolver(newPasswordRequiredSchema),
    defaultValues: {
      newPassword: "",
      confirmPassword: "",
    },
  })

  const handleSubmit = async (data: NewPasswordRequiredFormData) => {
    setIsLoading(true)
    try {
      const result = await confirmSignIn(data.newPassword)
      if (result.isSignedIn) {
        await fetchAuthSession({ forceRefresh: true })
        onSuccess()
      }
    } catch (error) {
      const errorMessage = mapCognitoError(error)
      message.error(errorMessage)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <>
      <div className="mb-6 text-center">
        <Title level={4}>Set a new password</Title>
        <Paragraph>
          Your account requires a new password. Please create one to continue.
        </Paragraph>
      </div>

      <Form form={form} onSubmit={handleSubmit}>
        <InputField
          name="newPassword"
          type="password"
          label="New Password"
          placeholder="Enter new password"
          required
        />
        <InputField
          name="confirmPassword"
          type="password"
          label="Confirm Password"
          placeholder="Confirm new password"
          required
        />
        <Button
          type="primary"
          htmlType="submit"
          loading={isLoading}
          className="w-full mb-3"
          data-testid="new-password-submit-button"
        >
          Set Password
        </Button>
      </Form>

      <div className="flex justify-start">
        <Button
          type="link"
          onClick={onCancel}
          data-testid="new-password-cancel-button"
        >
          Cancel
        </Button>
      </div>
    </>
  )
}
